import { combineEpics, ofType } from 'redux-observable';
import { Observable, of } from 'rxjs';
import { filter, map, mapTo, switchMap } from 'rxjs/operators';

import { isCurrentPageSel } from '@location';
import { organizationPositionsSel } from '@shared';
import { SET_ORGANIZATION } from '@shared/duck/actions';
import {
  organizationPositionSel,
  parentOrgGuidSel,
} from '@shared/duck/selectors/general.selectors';
import { catchAndReport } from '@utils/rxjs/operators';

import {
  LOAD_DISTRICTS_REQUEST,
  ROUTE_COUNCIL_UNITS,
  UNITS_SEARCH_REDIRECT_REQUEST,
  UNITS_SEARCH_REQUEST,
  loadDistrictsError,
  loadDistrictsRequest,
  loadDistrictsResponse,
  setRecentUnit,
  unitsSearchError,
  unitsSearchResponse,
} from './actions';
import services from './services';

const fetchUnitsEpic$ = (action$, state$) =>
  action$.pipe(
    ofType(UNITS_SEARCH_REQUEST),
    switchMap(({ payload }) => {
      const organizationGuid = parentOrgGuidSel(state$.value);
      const { districtOrgGuid } = payload;

      return services
        .getUnits$({
          organizationGuid: districtOrgGuid || organizationGuid,
          ...payload,
        })
        .pipe(
          map(unitsSearchResponse),
          catchAndReport(err => of(unitsSearchError(err))),
        );
    }),
  );

const fetchUnitsRedirectEpic$ = action$ =>
  action$.pipe(
    ofType(UNITS_SEARCH_REDIRECT_REQUEST),
    switchMap(({ payload }) => {
      const { orgGuid, unitTypeId } = payload;

      return services
        .getUnits$({
          organizationGuid: orgGuid,
          unitTypeId,
        })
        .mergeMap(response => {
          const [unitFound] = response;

          if (unitFound) {
            return Observable.concat(Observable.of(setRecentUnit(unitFound)));
          }

          return Observable.of();
        })
        .catchAndReport(err => of(unitsSearchError(err)));
    }),
  );

const fetchDistrictsEpic$ = (action$, state$) =>
  action$.pipe(
    ofType(LOAD_DISTRICTS_REQUEST),
    switchMap(() => {
      const state = state$.value;
      const organizationGuid = parentOrgGuidSel(state);
      let orgGuid = organizationGuid;
      const organizationPosition = organizationPositionSel(state);
      const isUnitCommissioner =
        !!organizationPosition?.allRoles.includes('Unit Commissioner');
      const councilGuid = organizationPosition.councilguid;
      if (isUnitCommissioner && councilGuid) {
        orgGuid = councilGuid;
      }

      return services
        .getDistricts$(orgGuid, organizationPositionsSel(state))
        .pipe(
          map(loadDistrictsResponse),
          catchAndReport(err => of(loadDistrictsError(err))),
        );
    }),
  );

const reloadDistrictsEpic$ = (action$, state$) =>
  action$.pipe(
    ofType(SET_ORGANIZATION),
    filter(() => isCurrentPageSel(state$.value, ROUTE_COUNCIL_UNITS)),
    mapTo(loadDistrictsRequest()),
  );

export default combineEpics(
  fetchUnitsEpic$,
  fetchUnitsRedirectEpic$,
  fetchDistrictsEpic$,
  reloadDistrictsEpic$,
);
